import React from 'react'
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  FormLabel,
  List,
  ListItemButton,
  ListItemText,
  Radio,
  RadioGroup,
  Stack,
  TextField,
  Typography,
} from '@mui/material'
import type { ContactSummarySearchResult, ReassignIdentityResponse } from '../../api/contacts'
import { reassignIdentity, searchContacts } from '../../api/contacts'

export interface ReassignIdentityModalProps {
  open: boolean
  stationId: number
  identityId: number | null
  onClose: () => void
  onReassigned: (result: ReassignIdentityResponse) => void
}

const ReassignIdentityModal: React.FC<ReassignIdentityModalProps> = ({
  open,
  stationId,
  identityId,
  onClose,
  onReassigned,
}) => {
  const [mode, setMode] = React.useState<'existing' | 'new'>('existing')
  const [query, setQuery] = React.useState('')
  const [results, setResults] = React.useState<ContactSummarySearchResult[]>([])
  const [loading, setLoading] = React.useState(false)
  const [error, setError] = React.useState<string | null>(null)
  const [selectedContact, setSelectedContact] = React.useState<ContactSummarySearchResult | null>(null)
  const [newContact, setNewContact] = React.useState({ name: '', location: '', notes: '' })

  const fetchResults = React.useCallback(
    async (value: string) => {
      try {
        setLoading(true)
        const response = await searchContacts(stationId, value)
        setResults(response.items)
        setError(null)
      } catch (err) {
        const message = err instanceof Error ? err.message : 'Unable to search contacts.'
        setError(message)
      } finally {
        setLoading(false)
      }
    },
    [stationId],
  )

  React.useEffect(() => {
    if (!open) {
      return
    }
    setQuery('')
    setResults([])
    setSelectedContact(null)
    setNewContact({ name: '', location: '', notes: '' })
    setMode('existing')
    setError(null)
    void fetchResults('')
  }, [open, fetchResults])

  const handleSubmit = async () => {
    if (!identityId) {
      return
    }
    try {
      setLoading(true)
      let response: ReassignIdentityResponse | null = null
      if (mode === 'existing') {
        if (!selectedContact) {
          return
        }
        response = await reassignIdentity(identityId, {
          station_id: stationId,
          to_contact_id: selectedContact.id,
        })
      } else {
        response = await reassignIdentity(identityId, {
          station_id: stationId,
          new_contact: {
            name: newContact.name.trim() || undefined,
            location: newContact.location.trim() || undefined,
            notes: newContact.notes.trim() || undefined,
          },
        })
      }
      if (response) {
        onReassigned(response)
        onClose()
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Unable to reassign identity.'
      setError(message)
    } finally {
      setLoading(false)
    }
  }

  const canSubmit =
    mode === 'existing'
      ? Boolean(selectedContact && identityId)
      : Boolean(identityId && (newContact.name.trim().length > 0 || newContact.location.trim().length > 0))

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Reassign identity</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={3}>
          {error && <Alert severity="error">{error}</Alert>}
          <FormControl>
            <FormLabel id="reassign-mode-label">Choose destination</FormLabel>
            <RadioGroup
              aria-labelledby="reassign-mode-label"
              row
              value={mode}
              onChange={(event) => setMode(event.target.value as 'existing' | 'new')}
            >
              <FormControlLabel value="existing" control={<Radio />} label="Existing contact" />
              <FormControlLabel value="new" control={<Radio />} label="Create new contact" />
            </RadioGroup>
          </FormControl>
          {mode === 'existing' ? (
            <Stack spacing={2}>
              <TextField
                label="Search contacts"
                placeholder="Search by name, email, or notes"
                value={query}
                onChange={(event) => {
                  const value = event.target.value
                  setQuery(value)
                  void fetchResults(value)
                }}
                autoFocus
              />
              <List dense sx={{ maxHeight: 240, overflowY: 'auto' }}>
                {results.map((contact) => (
                  <ListItemButton
                    key={contact.id}
                    selected={selectedContact?.id === contact.id}
                    onClick={() => setSelectedContact(contact)}
                  >
                    <ListItemText
                      primary={contact.name || `Contact #${contact.id}`}
                      secondary={contact.location || 'No location'}
                    />
                  </ListItemButton>
                ))}
                {!loading && results.length === 0 && (
                  <Typography variant="body2" color="text.secondary" sx={{ p: 2 }}>
                    No contacts found.
                  </Typography>
                )}
              </List>
            </Stack>
          ) : (
            <Stack spacing={2}>
              <TextField
                label="Name"
                value={newContact.name}
                onChange={(event) => setNewContact((prev) => ({ ...prev, name: event.target.value }))}
                autoFocus
              />
              <TextField
                label="Location"
                value={newContact.location}
                onChange={(event) => setNewContact((prev) => ({ ...prev, location: event.target.value }))}
              />
              <TextField
                label="Notes"
                value={newContact.notes}
                onChange={(event) => setNewContact((prev) => ({ ...prev, notes: event.target.value }))}
                multiline
                minRows={3}
              />
            </Stack>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="inherit">
          Cancel
        </Button>
        <Button onClick={() => void handleSubmit()} variant="contained" disabled={!canSubmit || loading}>
          Reassign
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default ReassignIdentityModal
